import { useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { useTeleconsultoriaProfile } from "../../profile/TeleconsultoriaProfileContext";
import { organizationTokenFromId } from "../../services/teleconsultoriaScheduleService";
import { AffiliationRuleError } from "../services/affiliationRules";
import { createAffiliation } from "../services/teleconsultoriaAffiliationService";
import {
  discardAffiliationRequest,
  findPendingProviderRequest,
  requestProviderAffiliation,
} from "../services/affiliationRequestService";
import type { AffiliationActionResult, AffiliationOrganization } from "../types";

const GENERIC_ERROR_MESSAGE = "Não foi possível salvar. Tente novamente.";

/** Regra de negócio mostra a mensagem própria; erro técnico vira texto genérico. */
function toErrorMessage(error: unknown): string {
  if (error instanceof AffiliationRuleError) return error.message;
  console.error("useTeleconsultoriaSetup", error);
  return GENERIC_ERROR_MESSAGE;
}

/**
 * Tela inicial "Prestar ou Receber" (perfil "unassigned"):
 * - becomeProvider: adiciona a 1ª afiliada (a logada vira prestadora);
 * - requestProvider: pede afiliação a uma prestadora (fica pendente até ela aprovar);
 * - pendingRequest/cancelRequest: o pedido aguardando e o "Cancelar pedido";
 * - checkApproval: a aprovação não chega por notificação, então o usuário verifica.
 * Depois de salvar, recarrega o perfil: a tela troca sozinha (sem refresh).
 */
export function useTeleconsultoriaSetup() {
  const { access } = useSessionContext();
  const { organization } = useOrganizationContext();
  const { reload: reloadProfile } = useTeleconsultoriaProfile();
  const ownOrganization = useMemo<AffiliationOrganization>(
    () => ({ token: organizationTokenFromId(access?.organizationId), name: organization?.name || "" }),
    [access?.organizationId, organization?.name],
  );
  const ownToken = ownOrganization.token;

  const pendingTask = useAsyncTask({
    task: useCallback(() => findPendingProviderRequest(ownToken), [ownToken]),
  });
  const createTask = useAsyncTask({
    task: useCallback(
      (receiver: AffiliationOrganization) => createAffiliation({ provider: ownOrganization, receiver }),
      [ownOrganization],
    ),
  });
  const requestTask = useAsyncTask({
    task: useCallback(
      (provider: AffiliationOrganization) =>
        requestProviderAffiliation({ provider, receiver: ownOrganization }),
      [ownOrganization],
    ),
  });
  const cancelTask = useAsyncTask({ task: discardAffiliationRequest });

  const { run: runPending } = pendingTask;
  const reloadPending = useCallback(() => {
    void runPending().catch((error) => console.error("useTeleconsultoriaSetup.pending", error));
  }, [runPending]);
  useEffect(() => {
    if (ownToken) reloadPending();
  }, [ownToken, reloadPending]);

  const { run: runCreate } = createTask;
  const becomeProvider = useCallback(
    async (receiver: AffiliationOrganization): Promise<AffiliationActionResult> => {
      try {
        await runCreate(receiver);
        toast.success(`${receiver.name} agora recebe sua teleconsultoria.`);
        reloadProfile();
        return { isSuccess: true };
      } catch (error) {
        return { isSuccess: false, errorMessage: toErrorMessage(error) };
      }
    },
    [runCreate, reloadProfile],
  );

  const { run: runRequest } = requestTask;
  const requestProvider = useCallback(
    async (provider: AffiliationOrganization): Promise<AffiliationActionResult> => {
      try {
        await runRequest(provider);
        toast.success(`Pedido enviado para ${provider.name}. Você poderá marcar quando ela aprovar.`);
        reloadPending();
        return { isSuccess: true };
      } catch (error) {
        return { isSuccess: false, errorMessage: toErrorMessage(error) };
      }
    },
    [runRequest, reloadPending],
  );

  const { run: runCancel } = cancelTask;
  const pendingRequest = pendingTask.data;
  const cancelRequest = useCallback(async () => {
    if (!pendingRequest) return;
    try {
      await runCancel(pendingRequest.affiliation);
      toast.info("Pedido cancelado. Você pode escolher outra prestadora.");
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      reloadPending();
    }
  }, [pendingRequest, runCancel, reloadPending]);

  // Aprovado? O perfil vira "afiliada"; ainda aguardando, o pedido continua na tela.
  const checkApproval = useCallback(() => {
    reloadProfile();
    reloadPending();
  }, [reloadProfile, reloadPending]);

  return {
    becomeProvider,
    requestProvider,
    pendingRequest,
    isLoadingPending: pendingTask.loading,
    cancelRequest,
    checkApproval,
    isSaving: createTask.loading || requestTask.loading || cancelTask.loading,
  };
}
